// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
/**
 * Password strength, for the meter on sign-up, reset, and change password: what is left of a
 * password once the guessable parts are taken out (repeats, keyboard and alphabet runs, years,
 * common password words, the person's own name and email), in bits. The server runs the same
 * rules and refuses weak passwords (server/internal/auth/strength.go); both are checked against
 * server/test/conformance/fixtures/passwords.json.
 */
/** The fewest characters a password may have. */
export const MIN_PASSWORD_LENGTH = 10;
/** The least strength a new password may have. */
export const MIN_PASSWORD_BITS = 40;
/** The words most leaked passwords are built from. Keep in step with strength.go (a Go test checks). */
const commonPasswordWords = [
    "password", "passw0rd", "pass", "qwerty", "azerty", "qwertz", "letmein", "welcome", "admin",
    "login", "secret", "changeme", "default", "access", "guest", "root", "user", "test", "hello",
    "iloveyou", "love", "lovely", "baby", "angel", "princess", "dragon", "monkey", "master",
    "shadow", "superman", "batman", "starwars", "pokemon", "ninja", "matrix", "trustno", "whatever",
    "freedom", "forever", "nothing", "football", "baseball", "soccer", "hockey", "sunshine",
    "summer", "winter", "spring", "autumn", "flower", "cookie", "cheese", "chocolate", "banana",
    "orange", "purple", "yellow", "silver", "golden", "diamond", "money", "family", "friend",
    "beautiful", "computer", "internet", "google", "facebook", "apple", "samsung", "microsoft",
    "mustang", "harley", "hunter", "ranger", "killer", "charlie", "michael", "jennifer", "jordan",
    "maggie", "ginger", "pepper", "buster", "january", "february", "march", "april", "june", "july",
    "august", "september", "october", "november", "december", "monday", "tuesday", "wednesday",
    "thursday", "friday", "saturday", "sunday", "granite"
];
/** The orders a run of keys or letters can follow, either way. */
const keyboardRuns = [
    "abcdefghijklmnopqrstuvwxyz", "01234567890", "qwertyuiop", "asdfghjkl", "zxcvbnm",
    "qwertzuiop", "azertyuiop", "1qaz2wsx3edc4rfv5tgb6yhn7ujm8ik9ol0p",
];
/** Look-alike characters and the letters they stand for. */
const leet = { "0": "o", "1": "i", "3": "e", "4": "a", "5": "s", "7": "t", "@": "a", $: "s", "!": "i" };
/** A string's code points, as Go's runes: the server counts the same way (graphemes would not). */
function runes(s) {
    return Array.from(s);
}
/** A password's strength in bits; personal holds the person's name and email, whose parts do not count. */
export function passwordBits(password, personal = []) {
    const orig = runes(password);
    let lower = runes(password.toLowerCase());
    if (lower.length !== orig.length)
        lower = orig; // lower-casing changed the length: compare as typed
    const n = lower.length;
    const guessable = Array.from({ length: n }, () => false);
    // A guessable part still costs an attacker about one character: keep its first.
    const mark = (from, to) => {
        for (let i = from + 1; i < to; i++)
            guessable[i] = true;
    };
    const slice = (s, from, to) => s.slice(from, to).join("");
    // Repeats: "aaa", "abab", "123123".
    for (let k = 1; k <= 4; k++) {
        for (let i = 0; i + k <= n;) {
            let reps = 1;
            while (i + (reps + 1) * k <= n && slice(lower, i + reps * k, i + (reps + 1) * k) === slice(lower, i, i + k))
                reps++;
            if (reps >= 2 && reps * k >= 3) {
                mark(i, i + reps * k);
                i += reps * k;
            }
            else
                i++;
        }
    }
    // Runs along the alphabet, the digits, or a keyboard row, either way: "abcd", "4321", "qwer".
    for (const order of keyboardRuns) {
        const seq = runes(order);
        for (const dir of [1, -1]) {
            const next = (a, b) => seq.some((r, j) => r === a && seq[j + dir] === b);
            for (let i = 0; i < n;) {
                let j = i;
                while (j + 1 < n && next(lower[j] ?? "", lower[j + 1] ?? ""))
                    j++;
                if (j - i + 1 >= 3) {
                    mark(i, j + 1);
                    i = j + 1;
                }
                else
                    i++;
            }
        }
    }
    // Years: 1900-2099, all four digits.
    for (let i = 0; i + 4 <= n; i++) {
        if (/^(19|20)[0-9]{2}$/.test(slice(lower, i, i + 4)))
            for (let j = i; j < i + 4; j++)
                guessable[j] = true;
    }
    // Words: common password words and the person's own, also in look-alike spelling ("p4ssw0rd").
    const plain = lower.map((r) => leet[r] ?? r);
    for (const w of [...commonPasswordWords, ...personalWords(personal)]) {
        const len = runes(w).length;
        for (const s of [lower, plain]) {
            for (let i = 0; i + len <= n; i++)
                if (slice(s, i, i + len) === w)
                    mark(i, i + len);
        }
    }
    let left = 0;
    let lowerSet = false;
    let upperSet = false;
    let digitSet = false;
    let otherSet = false;
    for (const [i, r] of orig.entries()) {
        if (guessable[i])
            continue;
        left++;
        if (/\p{Ll}/u.test(r))
            lowerSet = true;
        else if (/\p{Lu}/u.test(r))
            upperSet = true;
        else if (/\p{Nd}/u.test(r))
            digitSet = true;
        else
            otherSet = true;
    }
    const pool = (lowerSet ? 26 : 0) + (upperSet ? 26 : 0) + (digitSet ? 10 : 0) + (otherSet ? 33 : 0);
    if (left === 0 || pool < 2)
        return 0;
    return left * Math.log2(pool);
}
/** Rates a password from 0 (too weak) to 4 (strong). */
export function passwordScore(password, personal = []) {
    const bits = passwordBits(password, personal);
    if (bits < 25)
        return 0;
    if (bits < MIN_PASSWORD_BITS)
        return 1;
    if (bits < 55)
        return 2;
    if (bits < 70)
        return 3;
    return 4;
}
/** Whether the server will take this password: long enough and not too easy to guess. */
export function passwordAcceptable(password, personal = []) {
    return runes(password).length >= MIN_PASSWORD_LENGTH && passwordBits(password, personal) >= MIN_PASSWORD_BITS;
}
/** A name and email split into the parts a person might build a password from. */
function personalWords(personal) {
    const out = [];
    for (let s of personal) {
        s = s.toLowerCase();
        const at = s.indexOf("@");
        if (at >= 0)
            s = `${s.slice(0, at)} ${s.slice(at + 1).split(".")[0] ?? ""}`;
        for (const w of s.split(/[^\p{L}\p{Nd}]+/u))
            if (runes(w).length >= 3)
                out.push(w);
    }
    return out;
}
//# sourceMappingURL=password.js.map